import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Search, Users, Mail, Phone, MapPin, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { http } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { UpgradeDialog } from "../components/UpgradeDialog";

const EMPTY = { name: "", phone: "", email: "", address: "" };
const FIELDS = [["name", "Name", "Maria Alvarez"], ["phone", "Phone", "(512) 555-0199"], ["email", "Email", "maria@example.com"], ["address", "Address", "418 Oak Street, Austin"]];

const AddForm = ({ onAdded }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) {
      toast.error("Add the customer's name.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await http.post("/customers", f);
      onAdded(data);
      setF(EMPTY);
      toast.success(`${data.name} saved.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the customer.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="add-customer-form" className="bg-white/80 backdrop-blur-md border border-[#E2DCD2] rounded-xl shadow-sm p-6">
      <h2 className="font-semibold text-[#1C1915] mb-4">Add a customer</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {FIELDS.map(([k, label, ph]) => (
          <div key={k} className={`space-y-1.5 ${k === "address" ? "sm:col-span-2" : ""}`}>
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">{label}{k === "name" ? "" : " (optional)"}</Label>
            <Input data-testid={`add-customer-${k}`} type={k === "email" ? "email" : "text"} placeholder={ph} value={f[k]} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} />
          </div>
        ))}
      </div>
      <Button data-testid="add-customer-submit" type="submit" disabled={busy} className="mt-5 rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white h-11 px-6">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Save customer
      </Button>
    </form>
  );
};

const CustomerCard = ({ c, onDelete, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ phone: c.phone || "", email: c.email || "", address: c.address || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await http.put(`/customers/${c.id}`, f);
      onSaved({ ...c, ...data });
      setEditing(false);
      toast.success(`${c.name} updated.`);
    } catch {
      toast.error("Could not update that customer.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="customer-card" className="bg-white/85 backdrop-blur-md border border-[#E2DCD2] rounded-xl p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="font-serif text-lg text-[#1B3A4B] truncate">{c.name}</div>
        <div className="flex shrink-0">
          {!editing && (
            <button data-testid="customer-edit" onClick={() => setEditing(true)} title="Edit customer" className="p-2 rounded-md text-[#A0998C] hover:text-[#1B3A4B] hover:bg-[#1B3A4B]/10 transition-colors">
              <Pencil className="w-4 h-4" />
            </button>
          )}
          <button data-testid="customer-delete" onClick={() => onDelete(c)} title="Delete customer" className="p-2 rounded-md text-[#A0998C] hover:text-[#9E2A2B] hover:bg-[#9E2A2B]/10 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {editing ? (
        <div className="mt-3 space-y-2">
          {[["phone", "Phone"], ["email", "Email"], ["address", "Address"]].map(([k, label]) => (
            <Input key={k} data-testid={`customer-edit-${k}`} placeholder={label} value={f[k]} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} className="h-9" />
          ))}
          <div className="flex gap-2 pt-1">
            <Button data-testid="customer-edit-save" onClick={save} disabled={busy} className="rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white h-9 px-4 text-sm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Save</>}
            </Button>
            <Button data-testid="customer-edit-cancel" variant="ghost" onClick={() => { setEditing(false); setF({ phone: c.phone || "", email: c.email || "", address: c.address || "" }); }} className="rounded-full h-9 px-4 text-sm">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-1 space-y-1">
          {c.email && <div className="text-sm text-[#5C564E] flex items-center gap-1.5 break-all"><Mail className="w-3.5 h-3.5 text-[#A6844A] shrink-0" /> {c.email}</div>}
          {c.phone && <div className="text-sm text-[#5C564E] flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#A6844A] shrink-0" /> {c.phone}</div>}
          {c.address && <div className="text-sm text-[#5C564E] flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#A6844A] shrink-0" /> {c.address}</div>}
        </div>
      )}
      {c.jobs?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="customer-jobs">
          {c.jobs.map((j, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-[#F4F0E8] border border-[#D5CFC2] text-[#5C564E]">{j}</span>)}
        </div>
      )}
    </div>
  );
};

export default function CustomersPage() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    http.get("/customers").then(({ data }) => setList(data.customers || [])).catch(() => setList([]));
  }, []);

  const added = (c) => setList((l) => [c, ...(l || []).filter((x) => x.id !== c.id)]);
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? Their saved quotes are not affected.`)) return;
    try {
      await http.delete(`/customers/${c.id}`);
      setList((l) => l.filter((x) => x.id !== c.id));
      toast.success("Customer deleted.");
    } catch {
      toast.error("Could not delete that customer.");
    }
  };
  const shown = (list || []).filter((c) => `${c.name} ${c.email} ${c.phone} ${c.address} ${(c.jobs || []).join(" ")}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen flex flex-col">
      <Header onUpgrade={() => setUpgradeOpen(true)} />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div className="bg-white/75 backdrop-blur-md border border-[#E2DCD2] rounded-2xl p-6 shadow-sm">
          <h1 className="font-serif text-4xl text-[#1B3A4B] flex items-center gap-3"><Users className="w-8 h-8 text-[#A6844A]" /> Saved customers</h1>
          <p className="text-[#5C564E] mt-1">Add customers here, or they're saved automatically with each quote. Pick them on any new quote to fill their details instantly.</p>
        </div>
        <AddForm onAdded={added} />
        <div>
          <div className="relative max-w-md mb-4">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A0998C]" />
            <Input data-testid="customers-search" className="pl-9 bg-white/90" placeholder="Search by name, job, location, email or phone" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {list === null ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#A6844A]" /></div>
          ) : shown.length === 0 ? (
            <p data-testid="customers-empty" className="bg-white/80 rounded-xl p-6 text-center text-[#5C564E]">{list.length ? "No customers match your search." : "No saved customers yet."}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="customers-list">
              {shown.map((c) => <CustomerCard key={c.id} c={c} onDelete={remove} onSaved={(u) => setList((l) => l.map((x) => (x.id === u.id ? u : x)))} />)}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </div>
  );
}
